<template>
    <div>
        <el-row>
            <el-col :span="17">
                <full-calendar
                    style="margin-top: -20px"
                    ref="bookingCalendar"
                    :config="config"
                    :header="header"
                    :defaultView="defaultView"
                    @event-render="renderBooking"
                    @event-drop="updateBooking"
                    @event-resize="updateBooking"
                    :events="calendarBookings"
                    class="highlight-day"
                />
            </el-col>
            <el-col :span="7">
                <div
                    class="el-form-card el-form-card--right--cal"
                    style="width: 100%; margin-top: 10px"
                >
                    <div class="el-form-card__header">
                        <div class="el-form-card__header-left-icon">
                            <img
                                class="inline-block mt-2"
                                src="/images/icon/bookings.png"
                            />
                        </div>
                        {{ $t('form.title.calendarSettings') }}
                    </div>
                    <div class="el-form-card__body">
                        <div class="el-form-card-mini">
                            <stc-unloading-areas-selctor
                                :unloadingAreas="unloadingAreas"
                                :showPass="
                                    currentContainer.AvailableBookingTypes &&
                                    currentContainer.AvailableBookingTypes.includes(
                                        'PASS'
                                    )
                                "
                                @unloading-area-checked="unloadingAreasChecked"
                            ></stc-unloading-areas-selctor>
                        </div>
                    </div>
                </div>
            </el-col>
        </el-row>
    </div>
</template>
<script>
import Vue from 'vue'
import _ from 'lodash'
import FullCalendar from '../Calender'
import draggable from 'vuedraggable'
import { DateTime } from 'luxon'
import Moment from 'moment'
import { extendMoment } from 'moment-range'
const moment = extendMoment(Moment)
import { mapGetters } from 'vuex'
import BookingEventComponent from '../bookings/BookingEvent'
import BookingNotificationEventComponent from '../bookings/BookingNotificationEvent'
import BookingCreateEventComponent from '../bookings/BookingCreateEvent'
import UnloadingAreasSelectorComponent from '../bookings/UnloadingAreasSelector.vue'

const BookingEvent = Vue.extend(BookingEventComponent)
const BookingNotificationEvent = Vue.extend(BookingNotificationEventComponent)
const BookingCreateEvent = Vue.extend(BookingCreateEventComponent)

export default {
    name: 'stc-day-calendar',
    components: {
        'full-calendar': FullCalendar,
        'stc-unloading-areas-selctor': UnloadingAreasSelectorComponent,
        draggable,
    },
    mixins: [],

    props: {
        currentActorId: {
            default: 'tempTentativeActor',
        },
    },

    computed: {
        config() {
            const scrollTime = DateTime.local().toLocaleString(
                DateTime.TIME_24_WITH_SECONDS
            )

            return {
                locale: this.selectedLanguage.isoName.substring(0, 2),
                scrollTime,
                firstDay: 1,
                now: new Date(),
                slotLabelFormat: 'HH:mm',
                timezone: 'local',
                eventOverlap: true,
                defaultView: this.defaultView,
                selectable: false,
                schedulerLicenseKey: 'GPL-My-Project-Is-Open-Source',
                dayHeaders: false,
                header: false,
            }
        },

        bookings() {
            let bookings = this.rawBookings.map((item) => {
                let accessSet = item.AccessSets[0]
                let schedulePart = accessSet.Schedules[0].ScheduleParts[0]

                return {
                    ...item,
                    DateFrom: DateTime.fromString(
                        schedulePart.ValidFrom,
                        this.dateTimeFormat
                    ),
                    DateTo: DateTime.fromString(
                        schedulePart.ValidTo,
                        this.dateTimeFormat
                    ),
                    UnloadingArea: accessSet.Checkpoint,
                    UnloadingAreaID: accessSet.UnloadingAreaID,
                    requestedResources: accessSet.RequestedResources,
                    PackageGroups: item.PackageGroups,
                }
            })

            const tempBooking = bookings.filter(
                (it) => it.ActorID === 'tempTentativeActor'
            )[0]

            bookings = bookings.filter(
                (item) => item.ActorType !== 'TENTATIVE' || item.Status !== 2
            )

            let filtered

            if (this.unloadingAreaList.length === 0) {
                filtered = []
            }

            if (bookings) {
                filtered = bookings.filter((item) => {
                    return this.unloadingAreaList.some((check) => {
                        if (check) {
                            return check.UnloadingAreaID == item.UnloadingAreaID
                        }
                        return check == item.UnloadingAreaID
                    })
                })
            }

            if (tempBooking) {
                filtered.push(tempBooking)
            }

            const isBookingChecked = filtered.filter((bookingItem) => {
                if (
                    !this.currentContainer.IsBookingCanOverlapped &&
                    (bookingItem.ActorType === 'TENTATIVE' ||
                        bookingItem.ActorType === 'SLOT') &&
                    tempBooking &&
                    bookingItem.ActorID !== tempBooking.ActorID &&
                    bookingItem.UnloadingAreaID ==
                        tempBooking.CurrentUnloadingAreaID
                ) {
                    const date1 = [
                        bookingItem.DateFrom.toISO(),
                        bookingItem.DateTo.toISO(),
                    ]
                    const date2 = [
                        tempBooking.DateFrom.toISO(),
                        tempBooking.DateFrom.toISO(),
                    ]

                    const range = moment.range(date1)
                    const range2 = moment.range(date2)

                    if (range.overlaps(range2)) {
                        return true
                    }
                }
            })
            if (isBookingChecked.length > 0) {
                this.$message({
                    type: 'error',
                    showClose: true,
                    message: this.$t('form.label.overlappedDates'),
                })
                // Update overlapping status in booking event level
                Events.$emit('update-overlapping-status', true)
            } else {
                // Update overlapping status in booking event level
                Events.$emit('update-overlapping-status', false)
            }

            return filtered
        },

        calendarBookings() {
            let bookings = this.bookings.map((booking) => {
                let diff = booking.DateTo.diff(booking.DateFrom).as('days')
                let sDate = booking.DateFrom.toISO()
                let eDate = booking.DateTo.toISO()
                if (diff >= 1) {
                    eDate = booking.DateTo.plus({ days: 1 }).toISO()
                }

                const editable =
                    booking.ActorID === this.currentActorId ||
                    booking.UpdatedActorID === this.currentActorId

                return {
                    ...booking,
                    title: booking.BookingReference,
                    start: sDate,
                    end: eDate,
                    allDay: diff >= 1,
                    showPopover: false,
                    editable: editable,
                }
            })
            return [...bookings]
        },
        ...mapGetters([
            'selectedLanguage',
            'selectedContainer',
            'collapseBooking',
        ]),
    },

    data() {
        const dateTimeFormat = process.env.VUE_APP_DATETIME_FORMAT
        return {
            header: {
                left: '',
                center: '',
                right: '',
            },
            rawBookings: [],
            dateTimeFormat,
            unloadingAreas: [],
            defaultView: 'agendaDay',
            today: DateTime.local().toLocaleString(DateTime.DATE_HUGE),
            tempActor: '',
            unloadingAreaList: [],
            currentContainer: '',
            actorToUpdate: '',
        }
    },

    async created() {
        Events.$on(
            'fetch-calendar-data',
            (container, unloadingAreas, tempActorData, currentDate) => {
                if (container) {
                    this.$nextTick(() => {
                        if (
                            this.$refs.bookingCalendar.$el.getElementsByClassName(
                                'fc-widget-header'
                            )[1]
                        ) {
                            this.$refs.bookingCalendar.$el
                                .getElementsByClassName('fc-widget-header')[1]
                                .remove()
                        }
                    })

                    this.currentContainer = container
                    this.unloadingAreas = unloadingAreas
                    this.tempActor = tempActorData
                    if (
                        this.tempActor &&
                        this.tempActor.AccessSets[0].Schedules[0]
                            .ScheduleParts[0].ValidFrom
                    ) {
                        // Dynamic scrollTime in full-calendar is not available in current version hence using JS. https://github.com/fullcalendar/fullcalendar/issues/3878
                        const scrollHour = DateTime.fromISO(
                            this.tempActor.AccessSets[0].Schedules[0]
                                .ScheduleParts[0].ValidFrom
                        ).hour
                        const scrollToElement = this.$refs.bookingCalendar.$el
                            .getElementsByClassName('fc-slats')[0]
                            .querySelectorAll(
                                'tr[data-time*="' + scrollHour + ':00"]'
                            )
                        const elementOffsetTop =
                            scrollToElement && scrollToElement.length > 0
                                ? scrollToElement[0].offsetTop
                                : 0
                        this.$refs.bookingCalendar.$el.getElementsByClassName(
                            'fc-scroller'
                        )[0].scrollTop = elementOffsetTop
                    }
                    this.fetchBookings(container.ContainerID, currentDate)
                } else {
                    this.rawBookings = []
                }
            }
        )
    },

    mounted() {
        // Remove the header of the day calendar programmatically because normall CSS affects the main Booking calendar view.
        this.$refs.bookingCalendar.$el
            .getElementsByClassName('fc-widget-header')[1]
            .remove()
    },

    beforeDestroy() {
        Events.$off('fetch-calendar-data')
    },

    methods: {
        renderBooking(booking, element) {
            this.hideCalendarPoppers()

            let instance = null

            if (!booking._id) {
                instance = new BookingCreateEvent({
                    propsData: {
                        booking,
                        element,
                    },
                })
            } else if (booking.event_type === 'NOTIFICATION') {
                instance = new BookingNotificationEvent({
                    propsData: {
                        booking,
                        element,
                    },
                })
            } else {
                instance = new BookingEvent({
                    propsData: {
                        readonly: true,
                        booking,
                        element,
                        filters: this.bookingDetailsArr,
                        trans: this.$t,
                        unloadingArea: this.findUnloadingAreaByID(
                            booking.UnloadingAreaID
                        ),
                    },
                })

                instance.$on('update-tentative-booking', (booking) =>
                    this.updateBooking(booking)
                )
            }

            instance.$mount()
            element.eq(0).find('.fc-content').html(instance.$el)
        },

        hideCalendarPoppers() {
            for (const popper of document.querySelectorAll('.tippy-popper')) {
                const instance = popper._tippy

                if (instance && instance.state.visible) {
                    instance.popperInstance.disableEventListeners()
                    instance.hide()
                }
            }
        },

        findUnloadingAreaByID(id) {
            return _.find(this.unloadingAreas, { UnloadingAreaID: id })
        },

        async fetchBookings(
            containerId = this.selectedContainer.ContainerID,
            currentDate
        ) {
            if (!currentDate) currentDate = new Date()

            this.$refs.bookingCalendar.fireMethod('gotoDate', currentDate)

            const dateTimeFrom = DateTime.fromISO(
                moment(currentDate).startOf('day').toDate().toISOString()
            ).toFormat(this.dateTimeFormat)

            const dateTimeTo = DateTime.fromISO(
                moment(currentDate).endOf('day').toDate().toISOString()
            ).toFormat(this.dateTimeFormat)

            const unloadingAreasIdList = this.unloadingAreas
                .filter((area) => area.IsActive)
                .map((area) => area.UnloadingAreaID)

            let { data } = await this.$http.post(`Actor/Search`, {
                ValidFrom: dateTimeFrom,
                ValidTo: dateTimeTo,
                ActorTypeFilter: ['PASS', 'SLOT', 'MILKRUN', 'TENTATIVE'],
                DestinationContainerFilter: [containerId],
                IncludeOptionalItems: ['PACKAGE_GROUPS_ITEMS'],
                UnloadingAreaFilter: unloadingAreasIdList,
            })
            if (this.tempActor) {
                data.push(this.tempActor)

                const updatedActorId = this.tempActor.UpdatedActorID
                if (updatedActorId) {
                    this.actorToUpdate = data.filter((obj) => {
                        return obj.ActorID === updatedActorId
                    })[0]
                    data = data.filter((obj) => {
                        return obj.ActorID !== updatedActorId
                    })
                }
            }
            this.rawBookings = data
        },

        async updateBooking(booking) {
            const filtered = this.bookings.filter((bookingItem) => {
                const currentUnloadingAreaID = booking.CurrentUnloadingAreaID
                    ? booking.CurrentUnloadingAreaID
                    : booking.AccessSets[0].UnloadingAreaID

                if (
                    !this.currentContainer.IsBookingCanOverlapped &&
                    (bookingItem.ActorType === 'TENTATIVE' ||
                        bookingItem.ActorType === 'SLOT' ||
                        bookingItem.ActorType === 'PROPOSED SLOT') &&
                    bookingItem.ActorID !== booking.ActorID &&
                    bookingItem.UnloadingAreaID == currentUnloadingAreaID
                ) {
                    const date1 = [
                        bookingItem.DateFrom.toISO(),
                        bookingItem.DateTo.toISO(),
                    ]
                    const date2 = [booking.start, booking.end]

                    const range = moment.range(date1)
                    const range2 = moment.range(date2)

                    if (range.overlaps(range2)) {
                        return true
                    }
                }
            })
            if (filtered.length > 0) {
                // Update overlapping status in booking event level
                Events.$emit('update-overlapping-status', true)
                this.$message({
                    type: 'error',
                    showClose: true,
                    message: this.$t('form.label.overlappedDates'),
                })

                return false
            }

            // Update overlapping status in booking event level
            Events.$emit('update-overlapping-status', false)

            const startDate = DateTime.fromISO(
                booking.start.toDate().toISOString()
            ).toFormat(this.dateTimeFormat)

            const endDate = DateTime.fromISO(
                booking.end
                    ? booking.end.toDate().toISOString()
                    : booking.start.toDate().toISOString()
            ).toFormat(this.dateTimeFormat)

            // Update final actor payload
            Events.$emit('actor-update', startDate, endDate)

            // Locally update & render the temp actor
            this.rawBookings.forEach((booking) => {
                if (booking.ActorID === 'tempTentativeActor') {
                    booking.AccessSets.forEach((accessSet) => {
                        accessSet.Schedules.forEach((schedule) => {
                            schedule.ScheduleParts.forEach((part) => {
                                part.ValidFrom = startDate
                                part.ValidTo = endDate
                            })
                        })
                    })
                }
            })
        },

        unloadingAreasChecked(data) {
            this.unloadingAreaList = data
        },
    },
}
</script>
<style>
.tippy-tooltip {
    z-index: 2000 !important;
}

.el-picker-panel time-select el-popper {
    z-index: 99999 !important;
}
</style>